import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Grid,
} from '@mui/material';
import { useWarbandStore } from '../../stores/warbandStore';
import { useStargraveCustomStore } from '../../stores/stargraveCustomStore';
import { formatStargraveBuffEffect } from '../../game/stargraveBuffRules';
import { StargraveBuff, StargraveStat } from '../../types';
import { CustomContentManager } from '../shared/CustomContentManager';

const statFields: Array<[StargraveStat, string]> = [
  ['move', 'Move'], ['fight', 'Fight'], ['shoot', 'Shoot'], ['armour', 'Armour'], ['will', 'Will'], ['health', 'Health'],
];

interface FormState {
  name: string;
  modifiers: Record<StargraveStat, string>;
  displayEffect: string;
  summary: string;
}

const emptyForm = (): FormState => ({
  name: '',
  modifiers: { move: '', fight: '', shoot: '', armour: '', will: '', health: '' },
  displayEffect: '',
  summary: '',
});

function buffToForm(buff: StargraveBuff): FormState {
  return {
    name: buff.name,
    modifiers: statFields.reduce((result, [stat]) => ({
      ...result,
      [stat]: buff.statModifiers?.[stat] !== undefined ? String(buff.statModifiers[stat]) : '',
    }), {} as Record<StargraveStat, string>),
    displayEffect: buff.displayEffect ?? '',
    summary: buff.summary,
  };
}

const BuffFormFields: React.FC<{
  form: FormState;
  setForm: React.Dispatch<React.SetStateAction<FormState>>;
}> = ({ form, setForm }) => (
  <Grid container spacing={2}>
    <Grid size={{ xs: 12 }}>
      <TextField label="Name" value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} fullWidth required size="small" />
    </Grid>
    {statFields.map(([stat, label]) => (
      <Grid size={{ xs: 4, sm: 2 }} key={stat}>
        <TextField label={label} type="number" value={form.modifiers[stat]} placeholder="0"
          onChange={(e) => setForm((f) => ({ ...f, modifiers: { ...f.modifiers, [stat]: e.target.value } }))}
          fullWidth size="small" />
      </Grid>
    ))}
    <Grid size={{ xs: 12 }}>
      <TextField label="Effect Text (overrides stat text)" value={form.displayEffect}
        onChange={(e) => setForm((f) => ({ ...f, displayEffect: e.target.value }))}
        fullWidth size="small" placeholder="e.g. +2 Fight vs Shooting" />
    </Grid>
    <Grid size={{ xs: 12 }}>
      <TextField label="Description" value={form.summary} onChange={(e) => setForm((f) => ({ ...f, summary: e.target.value }))} fullWidth multiline minRows={2} size="small" />
    </Grid>
  </Grid>
);

export const ManageStargraveCustomBuffsModal: React.FC = () => {
  const { ui, closeCustomBuffsModal } = useWarbandStore();
  const customBuffs = useStargraveCustomStore((s) => s.customBuffs);
  const addCustomBuff = useStargraveCustomStore((s) => s.addCustomBuff);
  const updateCustomBuff = useStargraveCustomStore((s) => s.updateCustomBuff);
  const deleteCustomBuff = useStargraveCustomStore((s) => s.deleteCustomBuff);

  return (
    <Dialog open={ui.customBuffsModalOpen} onClose={closeCustomBuffsModal} maxWidth="sm" fullWidth
      PaperProps={{ sx: { height: '85vh', display: 'flex', flexDirection: 'column' } }}>
      <DialogTitle>Custom Effects</DialogTitle>
      <DialogContent dividers sx={{ overflowY: 'auto', flex: 1 }}>
        {ui.customBuffsModalOpen && <CustomContentManager<StargraveBuff, FormState>
          itemLabel="Effect"
          items={customBuffs}
          getItemName={(buff) => buff.name}
          getItemSecondary={(buff) => formatStargraveBuffEffect(buff)}
          emptyForm={emptyForm}
          itemToForm={buffToForm}
          validate={(form) => (form.name.trim() ? null : 'Name is required.')}
          renderFormFields={(form, setForm) => <BuffFormFields form={form} setForm={setForm} />}
          onSave={(form, editingId) => {
            const statModifiers: Partial<Record<StargraveStat, number>> = {};
            for (const [stat] of statFields) {
              const raw = form.modifiers[stat].trim();
              if (raw === '') continue;
              const value = Number(raw);
              if (!Number.isNaN(value) && value !== 0) statModifiers[stat] = value;
            }
            const data: Omit<StargraveBuff, 'id' | 'isCustom'> = {
              name: form.name.trim(),
              source: 'manual',
              statModifiers: Object.keys(statModifiers).length > 0 ? statModifiers : undefined,
              displayEffect: form.displayEffect.trim() || undefined,
              summary: form.summary.trim() || form.name.trim(),
            };
            if (editingId) updateCustomBuff(editingId, data);
            else addCustomBuff(data);
          }}
          onDelete={deleteCustomBuff}
          deleteMessage={(buff) => `Delete "${buff.name}"? Crew members with this effect active will lose it from display. This cannot be undone.`}
        />}
      </DialogContent>
      <DialogActions>
        <Button onClick={closeCustomBuffsModal}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

